import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { type Arena } from '@/types/arena.type'
import { Card, CardContent } from '@/components/ui/card'
import { MoreHorizontal, Eye, Pencil, Trash2, ImageOff } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface ArenaTableProps {
  arenas: Arena[]
  loading: boolean
  onView?: (arena: Arena) => void
  onEdit?: (arena: Arena) => void
  onDelete?: (arena: Arena) => void
}

function EmptyState() {
  return (
    <Card className="p-0">
      <CardContent>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="text-muted-foreground mb-2">
              ไม่พบข้อมูลสนามแข่งขัน
            </div>
            <div className="text-sm text-muted-foreground">
              ลองปรับเปลี่ยนคำค้นหา หรือเพิ่มสนามแข่งขันใหม่
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function LoadingState() {
  return (
    <Card className="p-0">
      <CardContent className="p-0">
        <div className="flex items-center justify-center py-8">
          <div className="text-muted-foreground">กำลังโหลด...</div>
        </div>
      </CardContent>
    </Card>
  )
}

export default function ArenaTable({
  arenas: initialArenas,
  loading,
  onView,
  onEdit,
  onDelete,
}: ArenaTableProps) {
  const [arenas, setArenas] = useState<Arena[]>(initialArenas)

  useEffect(() => {
    setArenas(initialArenas)
  }, [initialArenas])

  if (loading) return <LoadingState />
  if (arenas.length === 0) return <EmptyState />

  return (
    <Card className="p-0">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">รูปภาพ</TableHead>
                <TableHead>ข้อมูลสนาม</TableHead>
                <TableHead className="w-40">เบอร์ติดต่อ</TableHead>
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {arenas.map((arena) => (
                <TableRow key={arena.id} className="hover:bg-muted/50">
                  <TableCell>
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                      {arena.mediaUrls && arena.mediaUrls.length > 0 ? (
                        <img
                          src={arena.mediaUrls[0].url || ''}
                          alt={arena.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageOff className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-base font-medium text-black truncate max-w-[200px]">
                      {arena.name}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm truncate max-w-[150px]">
                      {arena.contactNumber || '-'}
                    </p>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {onView && (
                          <DropdownMenuItem onClick={() => onView(arena)}>
                            <Eye className="mr-2 h-4 w-4" />
                            ดูรายละเอียด
                          </DropdownMenuItem>
                        )}
                        {onEdit && (
                          <DropdownMenuItem onClick={() => onEdit(arena)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            แก้ไข
                          </DropdownMenuItem>
                        )}
                        {onDelete && (
                          <DropdownMenuItem
                            onClick={() => onDelete(arena)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            ลบ
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
